import { parseActionFilters } from "../features/ops/actions/staffActions";
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, useLocation } from "@tanstack/react-router";
import { lazy, Suspense, type ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "../auth/AuthProvider";
import { CallbackPage, RequireAuth, SignInPage, validateSignInSearch } from "../auth/pages";
import { parseAutomationFilters } from "../features/automations/automations";
import { parseAlarmFilters } from "../features/alarms/alarms";
import { MAP_SCREEN } from "../features/map/ready";
import { parseOrgTab } from "../features/organisation/tabs";
import { parseFleetFilters } from "../features/ops/queries";
import { parseDebugTab } from "../features/ops/debug/tabs";
import { parseDataAlarmFilter } from "../features/ops/dataAlarms/filter";
import { parseSupportSearch } from "../features/ops/support/search";
import { EnvironmentBar } from "../ui/EnvironmentBar";
import { Loading } from "../ui/Spinner";
import { AppShell } from "./AppShell";
import { OpsShell } from "./OpsShell";
import { EmbedPage } from "../features/embed/EmbedPage";
import { HomePage } from "../features/home/HomePage";
import { validateInvitationSearch } from "../features/invitation/invitation";

const DashboardPageLazy = lazy(() =>
  import("../features/dashboards/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const DashboardPage = (p: ComponentProps<typeof DashboardPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <DashboardPageLazy {...p} />
  </Suspense>
);

const DashboardsPageLazy = lazy(() =>
  import("../features/dashboards/DashboardsPage").then((m) => ({ default: m.DashboardsPage })),
);
const DashboardsPage = () => (
  <Suspense fallback={<Loading />}>
    <DashboardsPageLazy />
  </Suspense>
);

const MapPageLazy = lazy(() => import("../features/map/MapPage").then((m) => ({ default: m.MapPage })));
const MapPage = (p: ComponentProps<typeof MapPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <MapPageLazy {...p} />
  </Suspense>
);

const OpsHomeLazy = lazy(() => import("../features/ops/OpsHome").then((m) => ({ default: m.OpsHome })));
const OpsHome = () => (
  <Suspense fallback={<Loading />}>
    <OpsHomeLazy />
  </Suspense>
);

const DevicesPageLazy = lazy(() => import("../features/devices/DevicesPage").then((m) => ({ default: m.DevicesPage })));
const DevicesPage = () => (
  <Suspense fallback={<Loading />}>
    <DevicesPageLazy />
  </Suspense>
);

const SharedDeviceRouteLazy = lazy(() =>
  import("../features/devices/sharing/SharedWithMe").then((m) => ({ default: m.SharedDeviceRoute })),
);
const SharedDeviceRoute = (p: ComponentProps<typeof SharedDeviceRouteLazy>) => (
  <Suspense fallback={<Loading />}>
    <SharedDeviceRouteLazy {...p} />
  </Suspense>
);

const DevicePageLazy = lazy(() =>
  import("../features/devices/page/DevicePage").then((m) => ({ default: m.DevicePage })),
);
const DevicePage = (p: ComponentProps<typeof DevicePageLazy>) => (
  <Suspense fallback={<Loading />}>
    <DevicePageLazy {...p} />
  </Suspense>
);

const AlarmsPageLazy = lazy(() => import("../features/alarms/AlarmsPage").then((m) => ({ default: m.AlarmsPage })));
const AlarmsPage = (p: ComponentProps<typeof AlarmsPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <AlarmsPageLazy {...p} />
  </Suspense>
);

const AutomationPageLazy = lazy(() =>
  import("../features/automations/AutomationPage").then((m) => ({ default: m.AutomationPage })),
);
const AutomationPage = (p: ComponentProps<typeof AutomationPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <AutomationPageLazy {...p} />
  </Suspense>
);

const AutomationsPageLazy = lazy(() =>
  import("../features/automations/AutomationsPage").then((m) => ({ default: m.AutomationsPage })),
);
const AutomationsPage = (p: ComponentProps<typeof AutomationsPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <AutomationsPageLazy {...p} />
  </Suspense>
);

const CasePageLazy = lazy(() => import("../features/support/CasePage").then((m) => ({ default: m.CasePage })));
const CasePage = (p: ComponentProps<typeof CasePageLazy>) => (
  <Suspense fallback={<Loading />}>
    <CasePageLazy {...p} />
  </Suspense>
);

const HelpPageLazy = lazy(() => import("../features/support/HelpPage").then((m) => ({ default: m.HelpPage })));
const HelpPage = () => (
  <Suspense fallback={<Loading />}>
    <HelpPageLazy />
  </Suspense>
);

const NotificationsPageLazy = lazy(() =>
  import("../features/notifications/NotificationsPage").then((m) => ({ default: m.NotificationsPage })),
);
const NotificationsPage = () => (
  <Suspense fallback={<Loading />}>
    <NotificationsPageLazy />
  </Suspense>
);

const OrganisationPageLazy = lazy(() =>
  import("../features/organisation/OrganisationPage").then((m) => ({ default: m.OrganisationPage })),
);
const OrganisationPage = (p: ComponentProps<typeof OrganisationPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <OrganisationPageLazy {...p} />
  </Suspense>
);

const CustomersPageLazy = lazy(() =>
  import("../features/partners/CustomersPage").then((m) => ({ default: m.CustomersPage })),
);
const CustomersPage = () => (
  <Suspense fallback={<Loading />}>
    <CustomersPageLazy />
  </Suspense>
);

const StockPageLazy = lazy(() => import("../features/partners/StockPage").then((m) => ({ default: m.StockPage })));
const StockPage = () => (
  <Suspense fallback={<Loading />}>
    <StockPageLazy />
  </Suspense>
);

const ImportPageLazy = lazy(() => import("../features/ops/ImportPage").then((m) => ({ default: m.ImportPage })));
const ImportPage = (p: ComponentProps<typeof ImportPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <ImportPageLazy {...p} />
  </Suspense>
);

const InvitationPageLazy = lazy(() =>
  import("../features/invitation/InvitationPage").then((m) => ({ default: m.InvitationPage })),
);
const InvitationPage = (p: ComponentProps<typeof InvitationPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <InvitationPageLazy {...p} />
  </Suspense>
);

function NotFound() {
  const { t } = useTranslation();
  return <p className="p-8 text-sm">{t("error.notFound")}</p>;
}

// The console's pages are loaded on first use, so none of them is in the customer app's bundle (WEB-DBG-Q-02).
const FleetPageLazy = lazy(() => import("../features/ops/fleet/FleetPage").then((m) => ({ default: m.FleetPage })));
const FleetHealthLazy = lazy(() =>
  import("../features/ops/fleet/FleetHealthPage").then((m) => ({ default: m.FleetHealthPage })),
);
const StaffActionsLazy = lazy(() =>
  import("../features/ops/actions/StaffActionsPage").then((m) => ({ default: m.StaffActionsPage })),
);
const ServerHealthLazy = lazy(() =>
  import("../features/ops/server/ServerHealthPage").then((m) => ({ default: m.ServerHealthPage })),
);
const TrafficLazy = lazy(() => import("../features/ops/traffic/TrafficPage").then((m) => ({ default: m.TrafficPage })));
const SupportLazy = lazy(() => import("../features/ops/support/SupportPage").then((m) => ({ default: m.SupportPage })));
const CaseLazy = lazy(() => import("../features/ops/support/CasePage").then((m) => ({ default: m.CasePage })));
const DebugPageLazy = lazy(() => import("../features/ops/debug/DebugPage").then((m) => ({ default: m.DebugPage })));
const VirtualListLazy = lazy(() =>
  import("../features/ops/virtual/VirtualListPage").then((m) => ({ default: m.VirtualListPage })),
);
const VirtualNewLazy = lazy(() =>
  import("../features/ops/virtual/VirtualNewPage").then((m) => ({ default: m.VirtualNewPage })),
);
const FirmwareLazy = lazy(() =>
  import("../features/ops/firmware/FirmwarePage").then((m) => ({ default: m.FirmwarePage })),
);
const UploadLazy = lazy(() => import("../features/ops/firmware/UploadPage").then((m) => ({ default: m.UploadPage })));
const ReleasesLazy = lazy(() =>
  import("../features/ops/releases/ReleasesPage").then((m) => ({ default: m.ReleasesPage })),
);
const ReleaseLazy = lazy(() =>
  import("../features/ops/releases/ReleasePage").then((m) => ({ default: m.ReleasePage })),
);
const ReleaseEditorLazy = lazy(() =>
  import("../features/ops/releases/ReleaseEditor").then((m) => ({ default: m.ReleaseEditor })),
);
const CampaignLazy = lazy(() =>
  import("../features/ops/firmware/CampaignPage").then((m) => ({ default: m.CampaignPage })),
);
const AccountsLazy = lazy(() =>
  import("../features/ops/accounts/AccountsPage").then((m) => ({ default: m.AccountsPage })),
);
const MailSettingsLazy = lazy(() =>
  import("../features/ops/mail/MailSettingsPage").then((m) => ({ default: m.MailSettingsPage })),
);
const WeatherDataLazy = lazy(() =>
  import("../features/ops/weatherData/WeatherDataPage").then((m) => ({ default: m.WeatherDataPage })),
);
const ForecastSettingsLazy = lazy(() =>
  import("../features/ops/forecasts/ForecastSettingsPage").then((m) => ({ default: m.ForecastSettingsPage })),
);
const BridgesLazy = lazy(() => import("../features/ops/bridges/BridgesPage").then((m) => ({ default: m.BridgesPage })));
const BridgeFormLazy = lazy(() =>
  import("../features/ops/bridges/BridgeFormPage").then((m) => ({ default: m.BridgeFormPage })),
);
const BridgeLazy = lazy(() => import("../features/ops/bridges/BridgePage").then((m) => ({ default: m.BridgePage })));
const MappingLazy = lazy(() => import("../features/ops/bridges/MappingPage").then((m) => ({ default: m.MappingPage })));
const BridgedProductLazy = lazy(() =>
  import("../features/ops/bridges/ProductPage").then((m) => ({ default: m.ProductPage })),
);
const AddCloneLazy = lazy(() =>
  import("../features/ops/devicedata/AddClonePage").then((m) => ({ default: m.AddClonePage })),
);
const DeviceImportOpsLazy = lazy(() =>
  import("../features/ops/devicedata/DeviceImportOpsPage").then((m) => ({ default: m.DeviceImportOpsPage })),
);
// The automation builder is loaded on first use, so the customer app's first route stays within its budget.
const AutomationEditorLazy = lazy(() =>
  import("../features/automations/AutomationEditor").then((m) => ({ default: m.AutomationEditor })),
);
const AutomationEditor = (p: ComponentProps<typeof AutomationEditorLazy>) => (
  <Suspense fallback={<Loading />}>
    <AutomationEditorLazy {...p} />
  </Suspense>
);
const FleetPage = (p: ComponentProps<typeof FleetPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <FleetPageLazy {...p} />
  </Suspense>
);
const DebugPage = (p: ComponentProps<typeof DebugPageLazy>) => (
  <Suspense fallback={<Loading />}>
    <DebugPageLazy {...p} />
  </Suspense>
);
const VirtualListPage = (p: ComponentProps<typeof VirtualListLazy>) => (
  <Suspense fallback={<Loading />}>
    <VirtualListLazy {...p} />
  </Suspense>
);
const VirtualNewPage = () => (
  <Suspense fallback={<Loading />}>
    <VirtualNewLazy />
  </Suspense>
);

/**
 * Every route under one layout, so the development bar (WEB-SRV-BAR-01) also tops the sign-in callback, loading and
 * error states that render before a shell does.
 */
function Root() {
  // The phone apps' web-view widget shows the widget alone (mobile-dashboards-2 MD2-03).
  const embedded = useLocation().pathname.startsWith("/embed/");
  return (
    <div className="flex h-full flex-col">
      {!embedded && <EnvironmentBar />}
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFound });

// One widget of a dashboard for the phone apps' web-view widget (mobile-dashboards-2 MD2-03): outside the customer
// realm's sign-in, signed in with the phone app's own token (src/embed/session.ts).
const embedWidget = createRoute({
  getParentRoute: () => rootRoute,
  path: "embed/widget/$dashboardId/$widgetId",
  component: function EmbedRoute() {
    const { dashboardId, widgetId } = embedWidget.useParams();
    return <EmbedPage dashboardId={dashboardId} widgetId={widgetId} />;
  },
});

// Customer app, `customers` realm.
const customers = createRoute({
  getParentRoute: () => rootRoute,
  id: "customers",
  component: () => (
    <AuthProvider realm="customers">
      <Outlet />
    </AuthProvider>
  ),
});
const signIn = createRoute({
  getParentRoute: () => customers,
  path: "sign-in",
  validateSearch: validateSignInSearch,
  component: function CustomerSignIn() {
    return <SignInPage search={signIn.useSearch()} />;
  },
});
const callback = createRoute({ getParentRoute: () => customers, path: "auth/callback", component: CallbackPage });
// Where the invitation mail leads (customer-invitations CI-05): signed in, but outside the shell, which would show "no
// organisation" before the invitation is accepted.
const invitation = createRoute({
  getParentRoute: () => customers,
  path: "invitation",
  validateSearch: validateInvitationSearch,
  component: function InvitationRoute() {
    return (
      <RequireAuth>
        <InvitationPage search={invitation.useSearch()} />
      </RequireAuth>
    );
  },
});
const app = createRoute({
  getParentRoute: () => customers,
  id: "app",
  component: () => (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  ),
});
// The home page is where the app opens, after sign-in, a switch of organisation and from Home (specs/home-page HOME-02).
const home = createRoute({ getParentRoute: () => app, path: "/", component: HomePage });
const devices = createRoute({ getParentRoute: () => app, path: "devices", component: DevicesPage });
const device = createRoute({
  getParentRoute: () => app,
  path: "devices/$deviceId",
  component: function DeviceRoute() {
    const { deviceId } = device.useParams();
    return <DevicePage key={deviceId} deviceId={deviceId} />;
  },
});
const sharedDevice = createRoute({
  getParentRoute: () => app,
  path: "shared/$orgId/devices/$deviceId",
  component: function SharedDevice() {
    const { orgId, deviceId } = sharedDevice.useParams();
    return <SharedDeviceRoute key={`${orgId}/${deviceId}`} orgId={orgId} deviceId={deviceId} />;
  },
});
const map = createRoute({
  getParentRoute: () => app,
  path: "map",
  // ?place=<device> from the device page's "Place on map" (maps-2 MAP2-13).
  validateSearch: (search: Record<string, unknown>): { place?: string } =>
    typeof search.place === "string" ? { place: search.place } : {},
  component: MAP_SCREEN
    ? function MapRoute() {
        return <MapPage place={map.useSearch().place} />;
      }
    : () => <Navigate to="/devices" replace />,
});
const dashboards = createRoute({ getParentRoute: () => app, path: "dashboards", component: DashboardsPage });
const organisation = createRoute({
  getParentRoute: () => app,
  path: "organisation",
  validateSearch: parseOrgTab,
  component: function OrganisationRoute() {
    return <OrganisationPage tab={organisation.useSearch().tab} />;
  },
});
const alarms = createRoute({
  getParentRoute: () => app,
  path: "alarms",
  validateSearch: parseAlarmFilters,
  component: function AlarmsRoute() {
    const navigate = alarms.useNavigate();
    return <AlarmsPage filters={alarms.useSearch()} onFilters={(f) => void navigate({ search: f, replace: true })} />;
  },
});
// Automations (specs/web-automations WAU-01, -10, -12): the list with its filters in the URL, and one automation.
const automations = createRoute({
  getParentRoute: () => app,
  path: "automations",
  validateSearch: parseAutomationFilters,
  component: function AutomationsRoute() {
    const navigate = automations.useNavigate();
    return (
      <AutomationsPage
        filters={automations.useSearch()}
        onFilters={(f) => void navigate({ search: f, replace: true })}
      />
    );
  },
});
// The builder (WAU-20 to -25): ?device= when Add came from a device page, ?from= to duplicate one.
const automationNew = createRoute({
  getParentRoute: () => app,
  path: "automations/new",
  validateSearch: (search: Record<string, unknown>): { device?: string; from?: string } => ({
    ...(typeof search.device === "string" && search.device ? { device: search.device } : {}),
    ...(typeof search.from === "string" && search.from ? { from: search.from } : {}),
  }),
  component: function AutomationNewRoute() {
    const { device, from } = automationNew.useSearch();
    return <AutomationEditor key={`${device ?? ""}-${from ?? ""}`} mode="new" device={device} from={from} />;
  },
});
const automationEdit = createRoute({
  getParentRoute: () => app,
  path: "automations/$automationId/edit",
  component: function AutomationEditRoute() {
    const { automationId } = automationEdit.useParams();
    return <AutomationEditor key={automationId} mode="edit" automationId={automationId} />;
  },
});
const automation = createRoute({
  getParentRoute: () => app,
  path: "automations/$automationId",
  component: function AutomationRoute() {
    const { automationId } = automation.useParams();
    return <AutomationPage key={automationId} automationId={automationId} />;
  },
});
const partnerCustomers = createRoute({ getParentRoute: () => app, path: "customers", component: CustomersPage });
const partnerStock = createRoute({ getParentRoute: () => app, path: "stock", component: StockPage });
const help = createRoute({ getParentRoute: () => app, path: "help", component: HelpPage });
// The alarm e-mail switch (specs/alarm-email WEB-AE-01); the e-mail's footer links here.
const notifications = createRoute({ getParentRoute: () => app, path: "notifications", component: NotificationsPage });
const helpCase = createRoute({
  getParentRoute: () => app,
  path: "help/cases/$caseId",
  component: function CaseRoute() {
    const { caseId } = helpCase.useParams();
    return <CasePage key={caseId} caseId={caseId} />;
  },
});
const dashboard = createRoute({
  getParentRoute: () => app,
  path: "dashboards/$dashboardId",
  component: function DashboardRoute() {
    const { dashboardId } = dashboard.useParams();
    return <DashboardPage key={dashboardId} dashboardId={dashboardId} />;
  },
});

// Platform console, `ops` realm (WEB-01).
const ops = createRoute({
  getParentRoute: () => rootRoute,
  path: "ops",
  component: () => (
    <AuthProvider realm="ops">
      <Outlet />
    </AuthProvider>
  ),
});
const opsSignIn = createRoute({
  getParentRoute: () => ops,
  path: "sign-in",
  validateSearch: validateSignInSearch,
  component: function OpsSignIn() {
    return <SignInPage search={opsSignIn.useSearch()} />;
  },
});
const opsCallback = createRoute({ getParentRoute: () => ops, path: "auth/callback", component: CallbackPage });
const opsApp = createRoute({
  getParentRoute: () => ops,
  id: "console",
  component: () => (
    <RequireAuth>
      <OpsShell />
    </RequireAuth>
  ),
});
const opsHome = createRoute({ getParentRoute: () => opsApp, path: "/", component: OpsHome });
const opsImport = createRoute({
  getParentRoute: () => opsApp,
  path: "import",
  // From the unknown-devices list: the id the import is for (DTC-43).
  validateSearch: (s: Record<string, unknown>): { unit?: string } =>
    typeof s.unit === "string" && /^[0-9a-f]{32}$/i.test(s.unit) ? { unit: s.unit.toLowerCase() } : {},
  component: function ImportRoute() {
    return <ImportPage unit={opsImport.useSearch().unit} />;
  },
});
const opsFirmware = createRoute({
  getParentRoute: () => opsApp,
  path: "firmware",
  component: () => (
    <Suspense fallback={<Loading />}>
      <FirmwareLazy />
    </Suspense>
  ),
});
const opsFirmwareUpload = createRoute({
  getParentRoute: () => opsApp,
  path: "firmware/upload",
  component: () => (
    <Suspense fallback={<Loading />}>
      <UploadLazy />
    </Suspense>
  ),
});
const opsReleases = createRoute({
  getParentRoute: () => opsApp,
  path: "releases",
  component: () => (
    <Suspense fallback={<Loading />}>
      <ReleasesLazy />
    </Suspense>
  ),
});
const opsReleaseNew = createRoute({
  getParentRoute: () => opsApp,
  path: "releases/new",
  component: () => (
    <Suspense fallback={<Loading />}>
      <ReleaseEditorLazy />
    </Suspense>
  ),
});
const opsRelease = createRoute({
  getParentRoute: () => opsApp,
  path: "releases/$releaseId",
  component: function ReleaseRoute() {
    const { releaseId } = opsRelease.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <ReleaseLazy key={releaseId} releaseId={releaseId} />
      </Suspense>
    );
  },
});
const opsReleaseEdit = createRoute({
  getParentRoute: () => opsApp,
  path: "releases/$releaseId/edit",
  component: function ReleaseEditRoute() {
    const { releaseId } = opsReleaseEdit.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <ReleaseEditorLazy key={releaseId} releaseId={releaseId} />
      </Suspense>
    );
  },
});
const opsCampaign = createRoute({
  getParentRoute: () => opsApp,
  path: "dev-campaigns/$campaignId",
  component: function CampaignRoute() {
    const { campaignId } = opsCampaign.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <CampaignLazy key={campaignId} campaignId={campaignId} />
      </Suspense>
    );
  },
});
const PlansLazy = lazy(() => import("../features/ops/plans/PlansPage").then((m) => ({ default: m.PlansPage })));
const OrganisationsLazy = lazy(() =>
  import("../features/ops/organisations/OrganisationsPage").then((m) => ({ default: m.OrganisationsPage })),
);
const EnterprisePlanLazy = lazy(() =>
  import("../features/ops/organisations/EnterprisePlanPage").then((m) => ({ default: m.EnterprisePlanPage })),
);
const OrganisationViewLazy = lazy(() =>
  import("../features/ops/organisations/OrganisationView").then((m) => ({ default: m.OrganisationView })),
);
const OpsFeedLazy = lazy(() => import("../features/feed/OpsFeedPage").then((m) => ({ default: m.OpsFeedPage })));
const StaffLazy = lazy(() => import("../features/ops/staff/StaffPage").then((m) => ({ default: m.StaffPage })));
const opsPlans = createRoute({
  getParentRoute: () => opsApp,
  path: "plans",
  validateSearch: (s: Record<string, unknown>): { device?: string } =>
    typeof s.device === "string" && /^[0-9a-f-]{36}$/i.test(s.device) ? { device: s.device.toLowerCase() } : {},
  component: function PlansRoute() {
    const { device } = opsPlans.useSearch();
    const navigate = opsPlans.useNavigate();
    return (
      <Suspense fallback={<Loading />}>
        <PlansLazy device={device} onDevice={(id) => void navigate({ search: id ? { device: id } : {} })} />
      </Suspense>
    );
  },
});
// ?q= and ?email= come from the overview's search (console-overview OV-10): the list opens filtered, the account looked up.
const searchText = (v: unknown, max: number) =>
  typeof v === "string" && v.trim() ? v.trim().slice(0, max) : undefined;
const opsOrganisations = createRoute({
  getParentRoute: () => opsApp,
  path: "organisations",
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({ q: searchText(s.q, 100) }),
  component: function OrganisationsRoute() {
    return (
      <Suspense fallback={<Loading />}>
        <OrganisationsLazy initialQ={opsOrganisations.useSearch().q} />
      </Suspense>
    );
  },
});
// One organisation's settings, devices and subscriptions (specs/partners M2).
const opsOrganisation = createRoute({
  getParentRoute: () => opsApp,
  path: "organisations/$orgId",
  component: function OrganisationRoute() {
    const { orgId } = opsOrganisation.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <OrganisationViewLazy key={orgId} orgId={orgId} />
      </Suspense>
    );
  },
});
// One organisation's MQTT feed (specs/customer-feed WEB-FD-01): the name comes from the Organisations list's link.
const opsOrganisationFeed = createRoute({
  getParentRoute: () => opsApp,
  path: "organisations/$orgId/feed",
  validateSearch: (s: Record<string, unknown>): { name?: string } => ({ name: searchText(s.name, 200) }),
  component: function OrganisationFeedRoute() {
    const { orgId } = opsOrganisationFeed.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <OpsFeedLazy key={orgId} orgId={orgId} orgName={opsOrganisationFeed.useSearch().name} />
      </Suspense>
    );
  },
});
// One organisation's own Enterprise plan (plan-limits-2 PLR-12): the name comes from the Organisations list's link.
const opsOrganisationEnterprise = createRoute({
  getParentRoute: () => opsApp,
  path: "organisations/$orgId/enterprise-plan",
  validateSearch: (s: Record<string, unknown>): { name?: string } => ({ name: searchText(s.name, 200) }),
  component: function OrganisationEnterpriseRoute() {
    const { orgId } = opsOrganisationEnterprise.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <EnterprisePlanLazy key={orgId} orgId={orgId} orgName={opsOrganisationEnterprise.useSearch().name} />
      </Suspense>
    );
  },
});
const opsStaff = createRoute({
  getParentRoute: () => opsApp,
  path: "staff",
  component: () => (
    <Suspense fallback={<Loading />}>
      <StaffLazy />
    </Suspense>
  ),
});
const opsAccounts = createRoute({
  getParentRoute: () => opsApp,
  path: "accounts",
  validateSearch: (s: Record<string, unknown>): { email?: string } => ({ email: searchText(s.email, 254) }),
  component: function AccountsRoute() {
    return (
      <Suspense fallback={<Loading />}>
        <AccountsLazy initialEmail={opsAccounts.useSearch().email} />
      </Suspense>
    );
  },
});
const opsWeatherData = createRoute({
  getParentRoute: () => opsApp,
  path: "weather-data",
  component: () => (
    <Suspense fallback={<Loading />}>
      <WeatherDataLazy />
    </Suspense>
  ),
});
const opsForecastSettings = createRoute({
  getParentRoute: () => opsApp,
  path: "settings/forecasts",
  component: () => (
    <Suspense fallback={<Loading />}>
      <ForecastSettingsLazy />
    </Suspense>
  ),
});
// The MQTT data bridge (mqtt-bridge-editor WEB-BR-01 … WEB-BR-07).
const opsBridges = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges",
  component: () => (
    <Suspense fallback={<Loading />}>
      <BridgesLazy />
    </Suspense>
  ),
});
const opsBridgeNew = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges/new",
  component: () => (
    <Suspense fallback={<Loading />}>
      <BridgeFormLazy />
    </Suspense>
  ),
});
const opsBridge = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges/$bridgeId",
  component: function BridgeRoute() {
    const { bridgeId } = opsBridge.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <BridgeLazy key={bridgeId} bridgeId={bridgeId} />
      </Suspense>
    );
  },
});
const opsBridgeEdit = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges/$bridgeId/edit",
  component: function BridgeEditRoute() {
    const { bridgeId } = opsBridgeEdit.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <BridgeFormLazy key={bridgeId} bridgeId={bridgeId} />
      </Suspense>
    );
  },
});
const opsMappingNew = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges/$bridgeId/mappings/new",
  component: function MappingNewRoute() {
    const { bridgeId } = opsMappingNew.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <MappingLazy key={bridgeId} bridgeId={bridgeId} />
      </Suspense>
    );
  },
});
const opsMapping = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges/$bridgeId/mappings/$mappingId",
  component: function MappingRoute() {
    const { bridgeId, mappingId } = opsMapping.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <MappingLazy key={mappingId} bridgeId={bridgeId} mappingId={mappingId} />
      </Suspense>
    );
  },
});
const opsBridgedProductNew = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges/products/new",
  component: () => (
    <Suspense fallback={<Loading />}>
      <BridgedProductLazy />
    </Suspense>
  ),
});
const opsBridgedProduct = createRoute({
  getParentRoute: () => opsApp,
  path: "bridges/products/$name",
  component: function BridgedProductRoute() {
    const { name } = opsBridgedProduct.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <BridgedProductLazy key={name} name={name} />
      </Suspense>
    );
  },
});
// CreaNod1-Clone and staff device data (old-device-import OD-12, HI-07).
const opsCloneNew = createRoute({
  getParentRoute: () => opsApp,
  path: "clones/new",
  component: () => (
    <Suspense fallback={<Loading />}>
      <AddCloneLazy />
    </Suspense>
  ),
});
const opsDeviceImport = createRoute({
  getParentRoute: () => opsApp,
  path: "device-import",
  component: () => (
    <Suspense fallback={<Loading />}>
      <DeviceImportOpsLazy />
    </Suspense>
  ),
});
const opsMailSettings = createRoute({
  getParentRoute: () => opsApp,
  path: "settings/mail",
  component: () => (
    <Suspense fallback={<Loading />}>
      <MailSettingsLazy />
    </Suspense>
  ),
});
// The foundation's Stock list became the fleet view's "In stock" filter (P-110).
const opsStock = createRoute({
  getParentRoute: () => opsApp,
  path: "stock",
  component: () => <Navigate to="/ops/fleet" search={{ state: "stock" }} replace />,
});
const opsFleet = createRoute({
  getParentRoute: () => opsApp,
  path: "fleet",
  validateSearch: parseFleetFilters,
  component: function FleetRoute() {
    const navigate = opsFleet.useNavigate();
    return <FleetPage filters={opsFleet.useSearch()} onFilters={(f) => void navigate({ search: f })} />;
  },
});
const opsFleetHealth = createRoute({
  getParentRoute: () => opsApp,
  path: "fleet-health",
  component: () => (
    <Suspense fallback={<Loading />}>
      <FleetHealthLazy />
    </Suspense>
  ),
});
// The staff action log (users-and-roles UR-43, bug log A10).
const opsActions = createRoute({
  getParentRoute: () => opsApp,
  path: "actions",
  validateSearch: parseActionFilters,
  component: function ActionsRoute() {
    const navigate = opsActions.useNavigate();
    return (
      <Suspense fallback={<Loading />}>
        <StaffActionsLazy filters={opsActions.useSearch()} onFilters={(f) => void navigate({ search: f })} />
      </Suspense>
    );
  },
});
const opsServer = createRoute({
  getParentRoute: () => opsApp,
  path: "server",
  component: () => (
    <Suspense fallback={<Loading />}>
      <ServerHealthLazy />
    </Suspense>
  ),
});
const opsTraffic = createRoute({
  getParentRoute: () => opsApp,
  path: "traffic",
  validateSearch: (s: Record<string, unknown>): { q?: string } =>
    typeof s.q === "string" && s.q.trim() ? { q: s.q.trim().slice(0, 100) } : {},
  component: function TrafficRoute() {
    return (
      <Suspense fallback={<Loading />}>
        <TrafficLazy q={opsTraffic.useSearch().q} />
      </Suspense>
    );
  },
});
const DeviceTypesLazy = lazy(() =>
  import("../features/ops/deviceTypes/DeviceTypesPage").then((m) => ({ default: m.DeviceTypesPage })),
);
const DeviceTypeLazy = lazy(() =>
  import("../features/ops/deviceTypes/DeviceTypePage").then((m) => ({ default: m.DeviceTypePage })),
);
const NewDeviceTypeLazy = lazy(() =>
  import("../features/ops/deviceTypes/NewDeviceTypePage").then((m) => ({ default: m.NewDeviceTypePage })),
);
const DataAlarmsLazy = lazy(() =>
  import("../features/ops/dataAlarms/DataAlarmsPage").then((m) => ({ default: m.DataAlarmsPage })),
);
const UnknownDevicesLazy = lazy(() =>
  import("../features/ops/dataAlarms/UnknownDevicesPage").then((m) => ({ default: m.UnknownDevicesPage })),
);
// Device types, data alarms and unknown devices (device-type-console WEB-DTC-01 … WEB-DTC-14).
const opsDeviceTypes = createRoute({
  getParentRoute: () => opsApp,
  path: "device-types",
  component: () => (
    <Suspense fallback={<Loading />}>
      <DeviceTypesLazy />
    </Suspense>
  ),
});
const opsDeviceTypeNew = createRoute({
  getParentRoute: () => opsApp,
  path: "device-types/new",
  component: () => (
    <Suspense fallback={<Loading />}>
      <NewDeviceTypeLazy />
    </Suspense>
  ),
});
const opsDeviceType = createRoute({
  getParentRoute: () => opsApp,
  path: "device-types/$typeId",
  component: function DeviceTypeRoute() {
    const { typeId } = opsDeviceType.useParams();
    return (
      <Suspense fallback={<Loading />}>
        <DeviceTypeLazy key={typeId} typeId={typeId} />
      </Suspense>
    );
  },
});
// The support desk (console-support-desk CSD-01 … CSD-03): the queue, then one case.
const opsSupport = createRoute({
  getParentRoute: () => opsApp,
  path: "support",
  validateSearch: parseSupportSearch,
  component: function SupportRoute() {
    const navigate = opsSupport.useNavigate();
    return (
      <Suspense fallback={<Loading />}>
        <SupportLazy search={opsSupport.useSearch()} onSearch={(s) => void navigate({ search: s, replace: true })} />
      </Suspense>
    );
  },
});
const opsCase = createRoute({
  getParentRoute: () => opsApp,
  path: "support/$caseId",
  component: function CaseRoute() {
    return (
      <Suspense fallback={<Loading />}>
        <CaseLazy caseId={opsCase.useParams().caseId} />
      </Suspense>
    );
  },
});
const opsDataAlarms = createRoute({
  getParentRoute: () => opsApp,
  path: "data-alarms",
  validateSearch: parseDataAlarmFilter,
  component: function DataAlarmsRoute() {
    const search = opsDataAlarms.useSearch();
    const navigate = opsDataAlarms.useNavigate();
    return (
      <Suspense fallback={<Loading />}>
        <DataAlarmsLazy
          filter={{ state: "open", ...search }}
          onFilter={(f) => void navigate({ search: { ...f, state: f.state === "open" ? undefined : f.state } })}
        />
      </Suspense>
    );
  },
});
const opsUnknownDevices = createRoute({
  getParentRoute: () => opsApp,
  path: "unknown-devices",
  component: () => (
    <Suspense fallback={<Loading />}>
      <UnknownDevicesLazy />
    </Suspense>
  ),
});
const opsDevice = createRoute({
  getParentRoute: () => opsApp,
  path: "devices/$deviceId",
  component: function DeviceRoute() {
    const { deviceId } = opsDevice.useParams();
    return <Navigate to="/ops/devices/$deviceId/debug" params={{ deviceId }} replace />;
  },
});
const opsDebug = createRoute({
  getParentRoute: () => opsApp,
  path: "devices/$deviceId/debug",
  validateSearch: parseDebugTab,
  component: function DebugRoute() {
    const { deviceId } = opsDebug.useParams();
    const { tab } = opsDebug.useSearch();
    const navigate = opsDebug.useNavigate();
    return (
      <DebugPage
        key={deviceId}
        deviceId={deviceId}
        tab={tab}
        onTab={(next) => void navigate({ search: { tab: next }, replace: true })}
        onDeleted={(purged) => void navigate({ to: "/ops/virtual-devices", search: { purged } })}
      />
    );
  },
});
const opsVirtual = createRoute({
  getParentRoute: () => opsApp,
  path: "virtual-devices",
  validateSearch: (s: Record<string, unknown>): { purged?: string } =>
    typeof s.purged === "string" && /^[0-9a-f-]{36}$/i.test(s.purged) ? { purged: s.purged } : {},
  component: function VirtualListRoute() {
    return <VirtualListPage purged={opsVirtual.useSearch().purged} />;
  },
});
const opsVirtualNew = createRoute({
  getParentRoute: () => opsApp,
  path: "virtual-devices/new",
  component: VirtualNewPage,
});
// A virtual device's page is its debug page with the control strip (WEB-VD-PG-01).
const opsVirtualDevice = createRoute({
  getParentRoute: () => opsApp,
  path: "virtual-devices/$deviceId",
  validateSearch: parseDebugTab,
  component: function VirtualDeviceRoute() {
    const { deviceId } = opsVirtualDevice.useParams();
    const { tab } = opsVirtualDevice.useSearch();
    const navigate = opsVirtualDevice.useNavigate();
    return (
      <DebugPage
        key={deviceId}
        deviceId={deviceId}
        tab={tab}
        onTab={(next) => void navigate({ search: { tab: next }, replace: true })}
        onDeleted={(purged) => void navigate({ to: "/ops/virtual-devices", search: { purged } })}
      />
    );
  },
});

const routeTree = rootRoute.addChildren([
  embedWidget,
  customers.addChildren([
    signIn,
    callback,
    invitation,
    app.addChildren([
      home,
      devices,
      device,
      sharedDevice,
      map,
      dashboards,
      dashboard,
      alarms,
      automations,
      automationNew,
      automationEdit,
      automation,
      organisation,
      partnerCustomers,
      partnerStock,
      help,
      helpCase,
      notifications,
    ]),
  ]),
  ops.addChildren([
    opsSignIn,
    opsCallback,
    opsApp.addChildren([
      opsHome,
      opsImport,
      opsAccounts,
      opsPlans,
      opsOrganisations,
      opsOrganisation,
      opsOrganisationFeed,
      opsOrganisationEnterprise,
      opsStaff,
      opsMailSettings,
      opsForecastSettings,
      opsWeatherData,
      opsBridges,
      opsBridgeNew,
      opsBridge,
      opsBridgeEdit,
      opsMappingNew,
      opsMapping,
      opsBridgedProductNew,
      opsBridgedProduct,
      opsCloneNew,
      opsDeviceImport,
      opsFirmware,
      opsFirmwareUpload,
      opsReleases,
      opsReleaseNew,
      opsRelease,
      opsReleaseEdit,
      opsCampaign,
      opsStock,
      opsFleet,
      opsFleetHealth,
      opsTraffic,
      opsServer,
      opsActions,
      opsDeviceTypes,
      opsDeviceTypeNew,
      opsDeviceType,
      opsDataAlarms,
      opsSupport,
      opsCase,
      opsUnknownDevices,
      opsDevice,
      opsDebug,
      opsVirtual,
      opsVirtualNew,
      opsVirtualDevice,
    ]),
  ]),
]);

export function createAppRouter() {
  return createRouter({ routeTree, defaultPreload: false });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
